import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MessageService } from 'primeng/api';
import { CdkDragEnd } from '@angular/cdk/drag-drop';
import type { CdkDragMove } from '@angular/cdk/drag-drop';
import { StorageService } from 'src/app/services/storage.service';
import { DataTransferService } from 'src/app/doqfy-components/cat-edit/data-transfer.service';
import { SignatureStateService } from 'src/app/services/signature-state.service';
import {
  SIGNATURE_BOX_HEIGHT_PX,
  SIGNATURE_BOX_WIDTH_PX,
} from 'src/app/shared-components/signature-box.util';

export const SIGNER_PALETTE = [
  '#612e86', '#2E86AB', '#A23B72', '#F18F01', '#C73E1D',
  '#6A994E', '#BC4B51', '#5E60CE', '#E63946', '#06A77D',
  '#8338EC', '#FF006E', '#3A86FF', '#FB5607', '#FFBE0B',
];

export interface PredefinedReferenceMarker {
  name: string;
  positionLabel: string;
  xFraction: number;
  yFraction: number;
  widthFraction: number;
  heightFraction: number;
  pages: 'ALL' | number[];
}
@Component({
  selector: 'app-drag-drop',
  templateUrl: './drag-drop.component.html',
  styleUrls: ['./drag-drop.component.scss'],
  standalone: false,
})
export class DragDropComponent implements OnInit, OnDestroy {
  @Input() pdfSource: any;
  @Input() signatureDetails: any;
  @Input() signers: any = [];
  @Input() referenceMarkers: PredefinedReferenceMarker[] = [];
  @Output() submitEvent = new EventEmitter<any>();
  @Output() cancelEvent = new EventEmitter<any>();
  @ViewChild('pdfViewer') pdfViewer: any;

  convertedPDf!: string;
  pdfsizes: any[] = [];
  numPdfPages = 1;
  currentPage = 1;
  pageInput = 1;
  signerColors: string[] = [];

  signaturePlacementOptions = [
    { label: 'Current page', value: 'current' },
    { label: 'All pages', value: 'all' },
  ];

  // Cursor-carry state
  carryingSigner: any = null;
  ghostLeft = 0;
  ghostTop = 0;
  private cursorClientY = 0;
  private autoScrollRaf: number | null = null;
  private readonly SCROLL_THRESHOLD = 80;
  private readonly SCROLL_MAX_SPEED = 12;

  constructor(
    private messageService: MessageService,
    private storageService: StorageService,
    private dataTransferService: DataTransferService,
    private signatureStateService: SignatureStateService,
    private cdr: ChangeDetectorRef,
  ) {}

  // ─── Lifecycle ───────────────────────────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['signatureDetails'] && !changes['signatureDetails'].firstChange) {
      this.signatureDetails = changes['signatureDetails'].currentValue || {};
      this.syncSignatureStates();
    }
    if (changes['signers'] && !changes['signers'].firstChange) {
      this.generateSignerColors();
      this.assignReferenceColors();
    }
    if (changes['referenceMarkers'] && !changes['referenceMarkers'].firstChange) {
      this.assignReferenceColors();
    }
  }

  ngOnInit(): void {
    const filetype = this.storageService.getfileType();
    if (filetype === 'doc') {
      this.convertedPDf = this.convertPdfSource(this.dataTransferService.getDocPdfData());
    } else if (filetype === 'pdf') {
      this.convertedPDf = this.convertPdfSource(this.pdfSource);
    } else if (filetype === 'editPdf') {
      this.convertedPDf = this.pdfSource;
    }

    this.signatureDetails = this.signatureDetails || {};
    this.generateSignerColors();
    this.assignReferenceColors();
    this.syncSignatureStates();
  }

  ngOnDestroy(): void {
    this.stopAutoScroll();
  }

  // ─── PDF Loading ─────────────────────────────────────────────────────────────

  // Fires once, as soon as app-pdf-virtual-viewer has parsed the document —
  // it reads numPages directly from pdf.js, no per-page pre-measure loop
  // needed (that used to block the main thread for thousands of getPage()
  // calls on large documents).
  onDocLoaded(event: { numPages: number }): void {
    this.numPdfPages = event.numPages;
      this.currentPage = 1;
  this.pageInput = 1;
    this.cdr.detectChanges();
  }

  goToPage(page: number): void {
  if (!page || this.numPdfPages <= 0) {
    return;
  }

  const targetPage = Math.max(
    1,
    Math.min(Number(page), this.numPdfPages)
  );

  this.currentPage = targetPage;
  this.pageInput = targetPage;

  if (this.pdfViewer?.scrollToPage) {
    this.pdfViewer.scrollToPage(targetPage);
    return;
  }

  const pageElement = document.querySelector(
    `[data-page-number="${targetPage}"]`
  ) as HTMLElement | null;

  if (pageElement) {
    pageElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
}

  onPageRendered(event: { pageNum: number; widthPt: number; heightPt: number }): void {
    this.pdfsizes[event.pageNum - 1] = { width: event.widthPt, height: event.heightPt };
  }

  convertPtToPx(pt: number): number {
    return (4 / 3) * pt;
  }

  convertPdfSource(pdfSource: string): string {
    let cleaned = pdfSource.replace(/^"|"$/g, '');
    if (!cleaned.startsWith('data:application/pdf;base64,')) {
      cleaned = 'data:application/pdf;base64,' + cleaned;
    }
    return cleaned;
  }

  // ─── Signer Helpers ──────────────────────────────────────────────────────────

  getSignerListForPage(pageNum: number): any[] {
    return Array.isArray(this.signatureDetails[pageNum]) ? this.signatureDetails[pageNum] : [];
  }

  // ─── Pre-Defined Reference Markers (read-only) ────────────────────────────────

  hasReferenceMarkers(): boolean {
    return Array.isArray(this.referenceMarkers) && this.referenceMarkers.length > 0;
  }
  private referenceColors = new Map<PredefinedReferenceMarker, string>();
  private referenceStripes = new Map<PredefinedReferenceMarker, string>();

  private assignReferenceColors(): void {
    this.referenceColors.clear();
    this.referenceStripes.clear();
    if (!this.hasReferenceMarkers()) return;

   const offset = Array.isArray(this.signers) ? this.signers.length : 0;

    this.referenceMarkers.forEach((marker, i) => {
      const color = SIGNER_PALETTE[(offset + i) % SIGNER_PALETTE.length];
      this.referenceColors.set(marker, color);
      this.referenceStripes.set(
        marker,
        `repeating-linear-gradient(45deg, ${color}33 0, ${color}33 6px, ${color}0f 6px, ${color}0f 12px)`,
      );
    });
  }

  getReferenceColor(marker: PredefinedReferenceMarker): string {
    return this.referenceColors.get(marker) || SIGNER_PALETTE[0];
  }

  /** Diagonal stripe fill tinted with the marker's own color. */
  getReferenceStripes(marker: PredefinedReferenceMarker): string {
    return this.referenceStripes.get(marker) || 'none';
  }

  getReferenceMarkersForPage(pageNum: number): PredefinedReferenceMarker[] {
    if (!this.hasReferenceMarkers()) return [];
    return this.referenceMarkers.filter(
      (marker) =>
        marker &&
        (marker.pages === 'ALL' ||
          (Array.isArray(marker.pages) && marker.pages.includes(pageNum))),
    );
  }

  // A placed sign box is drawn at exactly the size of the signature that will be
  // stamped there (the template binds these to .sign-box), so the box the user
  // drags *is* the signature area. Also used for clamping to the page and for
  // collisions before the box exists in the DOM; once rendered, dragEnded() uses
  // its measured rect, which is the same size.
  readonly SIGN_BOX_WIDTH_PX = SIGNATURE_BOX_WIDTH_PX;
  readonly SIGN_BOX_HEIGHT_PX = SIGNATURE_BOX_HEIGHT_PX;

  // ─── Pre-Defined Collision Geometry ──────────────────────────────────────────
  //
  // Collisions are tested against the rectangles the user can actually see: the
  // placed .sign-box against the drawn .dd-reference-box. Nothing is inflated —
  // testing a padded or larger-than-drawn rect made boxes that merely sat *near* a
  // pre-defined slot report a clash and jump aside, which is worse than the overlap
  // it was guarding against, because the user cannot see why it happened.

  // Hairline intersections don't count. A box has to bite into a marker by more
  // than this on BOTH axes before it is treated as overlapping, so boxes that touch
  // or graze an edge are left exactly where they were dropped.
  private readonly COLLISION_TOLERANCE_PX = 4;

  // Breathing room left between a repositioned box and the marker it was pushed off.
  private readonly COLLISION_GAP_PX = 4;

  /** A marker's occupied rect in boundary pixels, exactly as it is drawn on the page. */
  private getMarkerRectPx(
    marker: PredefinedReferenceMarker,
    boundaryWidth: number,
    boundaryHeight: number,
  ): { left: number; top: number; right: number; bottom: number } {
    return {
      left: marker.xFraction * boundaryWidth,
      top: marker.yFraction * boundaryHeight,
      right: (marker.xFraction + marker.widthFraction) * boundaryWidth,
      bottom: (marker.yFraction + marker.heightFraction) * boundaryHeight,
    };
  }

  private findOverlappingReferenceMarker(
    pageNum: number,
    posX: number,
    posY: number,
    boxWidth: number,
    boxHeight: number,
    boundaryWidth: number,
    boundaryHeight: number,
  ): PredefinedReferenceMarker | null {
    const tolerance = this.COLLISION_TOLERANCE_PX;
    const boxRight = posX + boxWidth;
    const boxBottom = posY + boxHeight;

    return (
      this.getReferenceMarkersForPage(pageNum).find((marker) => {
        const rect = this.getMarkerRectPx(marker, boundaryWidth, boundaryHeight);

        // Measure the real intersection rather than a boolean "do they touch",
        // so the tolerance can be applied to how deep the overlap actually is.
        const overlapX = Math.min(boxRight, rect.right) - Math.max(posX, rect.left);
        const overlapY = Math.min(boxBottom, rect.bottom) - Math.max(posY, rect.top);

        return overlapX > tolerance && overlapY > tolerance;
      }) ?? null
    );
  }

  /**
   * Pre-defined slots are reserved: a custom box may not be dropped on top of one.
   * Given a requested position, returns the nearest position that clears every
   * marker on the page, along with the marker that was in the way (if any).
   *
   * `unresolved` means the page is too crowded to find a free spot nearby — the
   * caller keeps the requested position and tells the user to move it themselves,
   * which is still better than silently dropping the placement.
   */
  private resolveNonOverlappingPosition(
    pageNum: number,
    posX: number,
    posY: number,
    boxWidth: number,
    boxHeight: number,
    boundaryWidth: number,
    boundaryHeight: number,
  ): {
    x: number;
    y: number;
    clash: PredefinedReferenceMarker | null;
    unresolved: boolean;
  } {
    const clash = this.findOverlappingReferenceMarker(
      pageNum,
      posX,
      posY,
      boxWidth,
      boxHeight,
      boundaryWidth,
      boundaryHeight,
    );
    if (!clash) {
      return { x: posX, y: posY, clash: null, unresolved: false };
    }

    const gap = this.COLLISION_GAP_PX;
    const maxX = Math.max(0, boundaryWidth - boxWidth);
    const maxY = Math.max(0, boundaryHeight - boxHeight);
    const rect = this.getMarkerRectPx(clash, boundaryWidth, boundaryHeight);

    const seen = new Set<string>();
    const candidates: { x: number; y: number }[] = [];
    const addCandidate = (x: number, y: number): void => {
      const cx = Math.min(Math.max(0, x), maxX);
      const cy = Math.min(Math.max(0, y), maxY);
      const key = `${Math.round(cx)}:${Math.round(cy)}`;
      if (seen.has(key)) return;
      seen.add(key);
      candidates.push({ x: cx, y: cy });
    };

    // Slide off the marker along one axis, keeping the other axis where the user
    // dropped it, then the same four sides aligned to the marker's own edges.
    addCandidate(rect.right + gap, posY);
    addCandidate(rect.left - boxWidth - gap, posY);
    addCandidate(posX, rect.bottom + gap);
    addCandidate(posX, rect.top - boxHeight - gap);
    addCandidate(rect.right + gap, rect.top);
    addCandidate(rect.left - boxWidth - gap, rect.top);
    addCandidate(rect.left, rect.bottom + gap);
    addCandidate(rect.left, rect.top - boxHeight - gap);

    // Nearest to the original drop wins. Every candidate is re-tested against all
    // markers, not just the one we bounced off: the pre-defined grid columns sit
    // close together, so sliding clear of one slot often lands on its neighbour.
    const free = candidates
      .sort(
        (a, b) =>
          (a.x - posX) ** 2 + (a.y - posY) ** 2 - ((b.x - posX) ** 2 + (b.y - posY) ** 2),
      )
      .find(
        (candidate) =>
          !this.findOverlappingReferenceMarker(
            pageNum,
            candidate.x,
            candidate.y,
            boxWidth,
            boxHeight,
            boundaryWidth,
            boundaryHeight,
          ),
      );

    if (!free) {
      return { x: posX, y: posY, clash, unresolved: true };
    }
    return { x: free.x, y: free.y, clash, unresolved: false };
  }

  private notifyReservedPosition(
    clash: PredefinedReferenceMarker,
    pageNum: number,
    unresolved: boolean,
  ): void {
    const slot = `${clash.name}'s pre-defined ${clash.positionLabel} position on page ${pageNum}`;
    this.messageService.add({
      severity: 'error',
      summary: 'Position already reserved',
      detail: unresolved
        ? `This overlaps ${slot}, and there is no free space next to it. Please place the signature somewhere else.`
        : `This overlaps ${slot}, so the signature box was placed next to it instead.`,
      life: 5000,
    });
  }

  /**
   * Page size in boundary pixels. `pdfsizes` is filled per page as the virtual
   * viewer renders it, so a page that is still off-screen falls back to the first
   * known page size, then to A4 — enough for the collision maths on "all pages".
   */
  private getPageBoundaryPx(pageNum: number): { width: number; height: number } {
    const size =
      this.pdfsizes[pageNum - 1] ?? this.pdfsizes.find((entry) => !!entry) ?? null;
    return {
      width: this.convertPtToPx(size?.width ?? 595),
      height: this.convertPtToPx(size?.height ?? 842),
    };
  }

  getTotalCountForSigner(signer: any): number {
    const signerId = (signer?.absoluteIndex ?? this.signers.indexOf(signer)) + 1;
    let total = 0;
    Object.keys(this.signatureDetails).forEach((key) => {
      const pageData = this.signatureDetails[key];
      if (Array.isArray(pageData)) {
        const entry = pageData.find((s: any) => s?.id === signerId);
        if (entry?.signBoxList) total += entry.signBoxList.length;
      }
    });
    return total;
  }

  getSignerNameById(signerId: number): string {
    const match = this.signers.find(
      (s: any) => (s?.absoluteIndex ?? this.signers.indexOf(s)) + 1 === signerId,
    );
    return match?.name || match || '';
  }

  getSignerColor(signerId: number): string {
    const idx = this.signers.findIndex(
      (s: any) => (s?.absoluteIndex ?? this.signers.indexOf(s)) + 1 === signerId,
    );
    return this.signerColors[idx >= 0 ? idx : 0] || '#612e86';
  }

  getCarryingSignerColor(): string {
    if (!this.carryingSigner) return '#612e86';
    const idx = this.signers.indexOf(this.carryingSigner);
    return idx >= 0 ? (this.signerColors[idx] || '#612e86') : '#612e86';
  }

  isSignerReadOnly(signerId: number): boolean {
    const meta = this.signers.find(
      (s: any) => (s?.absoluteIndex ?? this.signers.indexOf(s)) + 1 === signerId,
    );
    return meta?.isReadOnly === true;
  }

  generateSignerColors(): void {
    this.signerColors = this.signers.map(
      (_: any, i: number) => SIGNER_PALETTE[i % SIGNER_PALETTE.length],
    );
  }

  private syncSignatureStates(): void {
    this.signers.forEach((signer: any, i: number) => {
      const absoluteIdx = signer?.absoluteIndex ?? i;
      const total = this.getTotalCountForSigner(signer);
      this.signatureStateService.updateSignatureState(absoluteIdx, total > 0, total);
    });
  }

  private ensurePageExists(pageNum: number): void {
    if (!this.signatureDetails[pageNum]) {
      this.signatureDetails[pageNum] = [];
    }
    for (let i = 0; i < this.signers.length; i++) {
      const absoluteIndex = this.signers[i]?.absoluteIndex ?? i;
      const signerId = absoluteIndex + 1;
      const exists = this.signatureDetails[pageNum].find((s: any) => s.id === signerId);
      if (!exists) {
        this.signatureDetails[pageNum].push({ id: signerId, signBoxList: [] });
      }
    }
  }

  // ─── Cursor-Carry Mechanic ────────────────────────────────────────────────────

  onSignerClick(event: MouseEvent, signer: any): void {
    event.stopPropagation();
    if (signer?.isReadOnly) return;
    this.carryingSigner = signer;
    this.startAutoScroll();
  }

  stopCarrying(): void {
    this.carryingSigner = null;
    this.stopAutoScroll();
  }

  onRootClick(): void {
    if (this.carryingSigner) this.stopCarrying();
  }

  onPageClick(event: MouseEvent, pageNum: number, boundary: HTMLElement): void {
    if (!this.carryingSigner) return;
    event.stopPropagation();

    const rect = boundary.getBoundingClientRect();
    const posX = Math.max(
      0,
      Math.min(event.clientX - rect.left, rect.width - this.SIGN_BOX_WIDTH_PX),
    );
    const posY = Math.max(
      0,
      Math.min(event.clientY - rect.top, rect.height - this.SIGN_BOX_HEIGHT_PX),
    );

    // Pre-defined slots are reserved — nudge the box clear of one rather than
    // stacking two signatures on the same spot.
    const resolved = this.resolveNonOverlappingPosition(
      pageNum,
      posX,
      posY,
      this.SIGN_BOX_WIDTH_PX,
      this.SIGN_BOX_HEIGHT_PX,
      rect.width,
      rect.height,
    );
    if (resolved.clash) {
      this.notifyReservedPosition(resolved.clash, pageNum, resolved.unresolved);
    }

    const signerId = (this.carryingSigner?.absoluteIndex ?? this.signers.indexOf(this.carryingSigner)) + 1;
    this.ensurePageExists(pageNum);

    const signerEntry = this.signatureDetails[pageNum].find((s: any) => s.id === signerId);
    signerEntry.signBoxList.push({
      id: signerEntry.signBoxList.length + 1,
      position: { x: resolved.x, y: resolved.y },
      placementOption: 'current',
      originalPageCreated: pageNum,
      globalId: `${signerId}_${Date.now()}_${Math.random()}`,
      hasPageSpecificPosition: false,
      pageSpecificPositions: {},
      excludedPages: new Set(),
    });

    const localIdx = this.signers.indexOf(this.carryingSigner);
    const absoluteIdx = this.carryingSigner?.absoluteIndex ?? localIdx;
    const total = this.getTotalCountForSigner(this.carryingSigner);
    this.signatureStateService.updateSignatureState(absoluteIdx, total > 0, total);

    this.stopCarrying();
    this.cdr.detectChanges();
  }

  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(event: MouseEvent): void {
    this.ghostLeft = event.clientX + 14;
    this.ghostTop = event.clientY + 14;
    this.cursorClientY = event.clientY;
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.stopCarrying();
  }

  // ─── Auto Scroll ─────────────────────────────────────────────────────────────

  private startAutoScroll(): void {
    const scrollEl = (document.querySelector('.modal-body') as HTMLElement) || document.documentElement;
    const loop = () => {
      if (!this.carryingSigner) { this.autoScrollRaf = null; return; }
      const rect = scrollEl.getBoundingClientRect();
      const y = this.cursorClientY;
      const top = rect.top;
      const bottom = rect.bottom;
      if (y < top + this.SCROLL_THRESHOLD) {
        scrollEl.scrollTop -= ((top + this.SCROLL_THRESHOLD - y) / this.SCROLL_THRESHOLD) * this.SCROLL_MAX_SPEED;
      } else if (y > bottom - this.SCROLL_THRESHOLD) {
        scrollEl.scrollTop += ((y - (bottom - this.SCROLL_THRESHOLD)) / this.SCROLL_THRESHOLD) * this.SCROLL_MAX_SPEED;
      }
      this.autoScrollRaf = requestAnimationFrame(loop);
    };
    this.stopAutoScroll();
    this.autoScrollRaf = requestAnimationFrame(loop);
  }

  private stopAutoScroll(): void {
    if (this.autoScrollRaf !== null) {
      cancelAnimationFrame(this.autoScrollRaf);
      this.autoScrollRaf = null;
    }
  }

  // ─── Sign Box Management ──────────────────────────────────────────────────────

  removeSignBox(signer: any, field: any, pageNum: number): void {
    const pageData = this.signatureDetails[pageNum];
    if (!pageData) return;

    const signerEntry = pageData.find((s: any) => s.id === signer.id);
    if (!signerEntry) return;

    const idx = signerEntry.signBoxList.findIndex((s: any) => s.id === field.id);
    if (idx < 0) return;
    signerEntry.signBoxList.splice(idx, 1);

    if (field.placementOption === 'all') {
      this.updateSignatureExclusionInAllPages(signer, field, pageNum);
      this.messageService.add({
        severity: 'info',
        summary: 'Signature Removed',
        detail: `Signature removed from page ${pageNum} only`,
      });
    }

    this.updateSignerState(signer.id);
    this.cdr.detectChanges();
  }

  private updateSignerState(signerId: number): void {
    const localIdx = this.signers.findIndex(
      (s: any) => (s?.absoluteIndex ?? this.signers.indexOf(s)) + 1 === signerId,
    );
    if (localIdx < 0) return;
    const signer = this.signers[localIdx];
    const absoluteIdx = signer?.absoluteIndex ?? localIdx;
    const total = this.getTotalCountForSigner(signer);
    this.signatureStateService.updateSignatureState(absoluteIdx, total > 0, total);
  }

  onSignaturePlacementChange(event: any, signer: any, signbox: any, pageNum: number): void {
    if (event.value === 'all') {
      this.applySignatureToAllPages(signer, signbox, pageNum);
      this.messageService.add({
        severity: 'success',
        summary: 'Signature Applied',
        detail: `Signature applied to all ${this.numPdfPages} pages`,
      });
    } else {
      this.removeSignatureFromOtherPages(signer, signbox, pageNum);
      this.messageService.add({
        severity: 'info',
        summary: 'Signature Updated',
        detail: 'Signature is now only on the current page',
      });
    }
    this.cdr.detectChanges();
  }

  applySignatureToAllPages(signer: any, signbox: any, currentPage: number): void {
    if (!signbox.excludedPages) signbox.excludedPages = new Set();

    // A spot that is free on the current page can still sit on a pre-defined slot
    // on another page, because a marker's `pages` list need not cover every page.
    const adjustedPages: number[] = [];

    for (let pageNum = 1; pageNum <= this.numPdfPages; pageNum++) {
      if (pageNum === currentPage) continue;
      if (signbox.excludedPages.has(pageNum)) continue;

      this.ensurePageExists(pageNum);

      let targetSigner = this.signatureDetails[pageNum].find((s: any) => s.id === signer.id);
      if (!targetSigner) {
        targetSigner = { id: signer.id, signBoxList: [] };
        this.signatureDetails[pageNum].push(targetSigner);
      }

      const exists = targetSigner.signBoxList.find((s: any) => s.globalId === signbox.globalId);
      if (!exists) {
        const basePos = signbox.hasPageSpecificPosition && signbox.pageSpecificPositions?.[pageNum]
          ? { ...signbox.pageSpecificPositions[pageNum] }
          : { ...signbox.position };

        const boundary = this.getPageBoundaryPx(pageNum);
        const resolved = this.resolveNonOverlappingPosition(
          pageNum,
          basePos.x,
          basePos.y,
          this.SIGN_BOX_WIDTH_PX,
          this.SIGN_BOX_HEIGHT_PX,
          boundary.width,
          boundary.height,
        );
        const pos = { x: resolved.x, y: resolved.y };

        if (resolved.clash) {
          adjustedPages.push(pageNum);
          // Record a per-page override too — getPosition() prefers it over
          // `position`, so without this the box would render back on the marker.
          signbox.pageSpecificPositions = signbox.pageSpecificPositions || {};
          signbox.pageSpecificPositions[pageNum] = { ...pos };
          signbox.hasPageSpecificPosition = true;
        }

        targetSigner.signBoxList.push({
          id: targetSigner.signBoxList.length + 1,
          position: pos,
          placementOption: 'all',
          originalPageCreated: signbox.originalPageCreated,
          globalId: signbox.globalId,
          hasPageSpecificPosition: signbox.hasPageSpecificPosition || false,
          pageSpecificPositions: signbox.pageSpecificPositions || {},
          excludedPages: signbox.excludedPages || new Set(),
        });
      }
    }

    if (adjustedPages.length > 0) {
      const pageList = adjustedPages.join(', ');
      this.messageService.add({
        severity: 'error',
        summary: 'Position already reserved',
        detail: `On ${adjustedPages.length === 1 ? 'page' : 'pages'} ${pageList} this spot is taken by a pre-defined position, so the signature box was placed next to it there.`,
        life: 6000,
      });
    }
  }

  removeSignatureFromOtherPages(signer: any, signbox: any, currentPage: number): void {
    for (let pageNum = 1; pageNum <= this.numPdfPages; pageNum++) {
      if (pageNum === currentPage || !this.signatureDetails[pageNum]) continue;
      const targetSigner = this.signatureDetails[pageNum].find((s: any) => s.id === signer.id);
      if (targetSigner) {
        const idx = targetSigner.signBoxList.findIndex((s: any) => s.globalId === signbox.globalId);
        if (idx >= 0) targetSigner.signBoxList.splice(idx, 1);
      }
    }
  }

  updateSignatureExclusionInAllPages(signer: any, signbox: any, excludedPageNum: number): void {
    for (let pageNum = 1; pageNum <= this.numPdfPages; pageNum++) {
      if (!this.signatureDetails[pageNum]) continue;
      const targetSigner = this.signatureDetails[pageNum].find((s: any) => s.id === signer.id);
      if (targetSigner) {
        const targetSig = targetSigner.signBoxList.find((s: any) => s.globalId === signbox.globalId);
        if (targetSig) {
          if (!targetSig.excludedPages) targetSig.excludedPages = new Set();
          targetSig.excludedPages.add(excludedPageNum);
        }
      }
    }
  }

  // ─── CDK Drag ─────────────────────────────────────────────────────────────────

  onDragMoved($event: CdkDragMove): void {
    const scrollEl = document.querySelector('.modal-body') as HTMLElement;
    if (!scrollEl) return;
    const rect = scrollEl.getBoundingClientRect();
    const y = $event.pointerPosition.y;
    const threshold = 80;
    const maxSpeed = 12;
    if (y < rect.top + threshold) {
      scrollEl.scrollTop -= ((rect.top + threshold - y) / threshold) * maxSpeed;
    } else if (y > rect.bottom - threshold) {
      scrollEl.scrollTop += ((y - (rect.bottom - threshold)) / threshold) * maxSpeed;
    }
  }

  dragEnded($event: CdkDragEnd, item: any, field: any, pageNum: number): void {
    const draggedEl = $event.source.element.nativeElement;
    const boundary = draggedEl.closest('.page-boundary') as HTMLElement;
    if (!boundary) return;

    const boundaryRect = boundary.getBoundingClientRect();
    const draggedRect = draggedEl.getBoundingClientRect();

    // Absolute position within boundary from the rendered rect (accounts for CDK transform)
    let posX = draggedRect.left - boundaryRect.left;
    let posY = draggedRect.top - boundaryRect.top;
    posX = Math.max(0, Math.min(posX, boundaryRect.width - draggedRect.width));
    posY = Math.max(0, Math.min(posY, boundaryRect.height - draggedRect.height));

    // Reset CDK transform — positioning is via [style.top/left], not CDK transform
    $event.source.reset();

    const pageData = this.signatureDetails[pageNum];
    if (!pageData) return;
    const signerEntry = pageData.find((s: any) => s.id === item.id);
    if (!signerEntry) return;
    const signBox = signerEntry.signBoxList.find((s: any) => s.id === field.id);
    if (!signBox) return;

    // A drop onto a reserved pre-defined slot is bounced to the nearest free spot.
    // $event.source.reset() above already cleared the CDK transform, so the box
    // renders at the corrected [style.top/left] straight away.
    // draggedRect is the box as actually rendered, so the test uses its real
    // size rather than the .sign-box minimums.
    const resolved = this.resolveNonOverlappingPosition(
      pageNum,
      posX,
      posY,
      draggedRect.width,
      draggedRect.height,
      boundaryRect.width,
      boundaryRect.height,
    );
    if (resolved.clash) {
      this.notifyReservedPosition(resolved.clash, pageNum, resolved.unresolved);
    }

    signBox.position = { x: resolved.x, y: resolved.y };
    if (field.placementOption === 'all') {
      signBox.hasPageSpecificPosition = true;
      signBox.pageSpecificPositions = signBox.pageSpecificPositions || {};
      signBox.pageSpecificPositions[pageNum] = { x: resolved.x, y: resolved.y };
    }
    this.cdr.detectChanges();
  }

  getPosition(field: any, pageNum: number): { x: number; y: number } {
    if (field.hasPageSpecificPosition && field.pageSpecificPositions?.[pageNum]) {
      return field.pageSpecificPositions[pageNum];
    }
    return { x: field.position?.x ?? 0, y: field.position?.y ?? 0 };
  }

  // ─── Submit / Cancel ──────────────────────────────────────────────────────────

  checkValidity(): boolean {
    return this.signers.some((signer: any) => {
      if (signer?.isReadOnly) return false;
      return this.getTotalCountForSigner(signer) <= 0;
    });
  }

  onSubmit(): void {
    for (const [key, value] of Object.entries(this.signatureDetails)) {
      const pageIdx = Number(key) - 1;
      if (this.pdfsizes[pageIdx]) {
        for (const v of Object.values(value as any)) {
          (v as any).dimension = this.pdfsizes[pageIdx];
          if (!(v as any).name) {
            const signerId = (v as any).id;
            const match = this.signers.find(
              (s: any) => (s?.absoluteIndex ?? this.signers.indexOf(s)) + 1 === signerId,
            );
            (v as any).name = match?.name || match || '';
          }
        }
      }
    }
    this.submitEvent.emit();
    this.syncSignatureStates();
  }

  onCancel(): void {
    this.stopCarrying();
    this.cancelEvent.emit();
  }
}
